import { Component, OnInit, Input, Output, EventEmitter, Inject } from '@angular/core';
import { BaseComponent } from '../classes/BaseComponent';
import { DOCUMENT } from '@angular/common';
import { PriceHelper } from '../helpers/PriceHelper';
import { ListHelper } from '../helpers/ListHelper';
import { Global } from '../classes/Global';

export interface PriceAlertSettings {
  hdPriceOption: 'under' | 'custom';
  hdCustomPrice: number | null;
  sdPriceOption: 'under' | 'custom' | 'ignore';
  sdCustomPrice: number | null;
  fourKPriceOption?: 'under' | 'custom';
  fourKCustomPrice?: number | null;
  purchaseType: 'buy' | 'rent';
  // Single price for items without SD/HD (music, apps, books)
  singlePriceOption?: 'under' | 'custom';
  singleCustomPrice?: number | null;
}

@Component({
  selector: 'app-price-alert-modal',
  templateUrl: './price-alert-modal.component.html',
  styleUrls: ['./price-alert-modal.component.scss']
})
export class PriceAlertModalComponent extends BaseComponent implements OnInit {

  isOpen = false;

  @Input() item: any;
  @Input() currency: string = 'EUR';
  @Input() store: string = '';

  @Output() onSave = new EventEmitter<PriceAlertSettings>();
  @Output() onClose = new EventEmitter();
  @Output() onRemove = new EventEmitter();

  // Price alert settings
  hdPriceOption: 'under' | 'custom' = 'under';
  hdCustomPrice: number | null = null;
  sdPriceOption: 'under' | 'custom' | 'ignore' = 'under';
  sdCustomPrice: number | null = null;
  fourKPriceOption: 'under' | 'custom' = 'under';
  fourKCustomPrice: number | null = null;
  purchaseType: 'buy' | 'rent' = 'buy';
  // Single price option for items without SD/HD (music, apps, books)
  singlePriceOption: 'under' | 'custom' = 'under';
  singleCustomPrice: number | null = null;

  constructor(
    @Inject(DOCUMENT) private dom: Document,
  ) {
    super();
  }

  ngOnInit() {
  }

  open() {
    this.isOpen = true;
    this.dom.body.style.overflow = 'hidden';
    this.dom.body.style.width = '100%';
    this.initializeDefaultPrices();
  }

  close() {
    this.isOpen = false;
    this.dom.body.style.overflow = '';
    this.dom.body.style.position = '';
    this.onClose.emit();
  }

  initializeDefaultPrices() {
    if (!this.item) {
      return;
    }

    // Get saved prices from wishlist (v2 API)
    // API returns: initialPriceValue (SD), initialHdPriceValue (HD), initial4kPriceValue (4K), hasCustomPrice, isRental
    const wishlistData = ListHelper.getWishlistCustomPrices(this.item, this.store);

    // Initialize HD price settings
    if (wishlistData.initialHdPriceValue !== null) {
      // Check if saved price differs from current price (means user set a custom price)
      if (this.item.priceHd && Math.abs(wishlistData.initialHdPriceValue - this.item.priceHd) > 0.01) {
        this.hdPriceOption = 'custom';
        this.hdCustomPrice = wishlistData.initialHdPriceValue;
      } else {
        this.hdPriceOption = 'under';
        this.hdCustomPrice = this.item.priceHd || null;
      }
    } else {
      this.hdPriceOption = 'under';
      this.hdCustomPrice = this.item.priceHd || null;
    }

    // Initialize SD price settings
    // initialPriceValue === -1 means SD is ignored
    if (wishlistData.initialPriceValue === -1) {
      this.sdPriceOption = 'ignore';
      this.sdCustomPrice = this.item.priceSd || null;
    } else if (wishlistData.initialPriceValue !== null) {
      // Check if saved price differs from current price
      if (this.item.priceSd && Math.abs(wishlistData.initialPriceValue - this.item.priceSd) > 0.01) {
        this.sdPriceOption = 'custom';
        this.sdCustomPrice = wishlistData.initialPriceValue;
      } else {
        this.sdPriceOption = 'under';
        this.sdCustomPrice = this.item.priceSd || null;
      }
    } else {
      this.sdPriceOption = 'under';
      this.sdCustomPrice = this.item.priceSd || null;
    }

    // Initialize 4K price settings (Vudu only) - no ignore option for 4K
    if (this.isVuduStore()) {
      const current4kPrice = this.purchaseType === 'rent' ? this.item.rentalPrice4k : this.item.price4k;
      // Check if saved price exists and differs from current price (custom price set)
      if (wishlistData.initial4kPriceValue !== null && wishlistData.initial4kPriceValue !== -1) {
        if (current4kPrice && Math.abs(wishlistData.initial4kPriceValue - current4kPrice) > 0.01) {
          this.fourKPriceOption = 'custom';
          this.fourKCustomPrice = wishlistData.initial4kPriceValue;
        } else {
          this.fourKPriceOption = 'under';
          this.fourKCustomPrice = current4kPrice || null;
        }
      } else {
        this.fourKPriceOption = 'under';
        this.fourKCustomPrice = current4kPrice || null;
      }
    }

    // Initialize purchase type (buy/rent) based on isRental flag from API
    this.purchaseType = wishlistData.isRental ? 'rent' : 'buy';

    // Initialize single price settings (for items without SD/HD like music, apps, books)
    if (this.hasSinglePrice()) {
      // For single price items, use initialPriceValue from API
      if (wishlistData.initialPriceValue !== null && wishlistData.initialPriceValue !== -1) {
        if (this.item.price && Math.abs(wishlistData.initialPriceValue - this.item.price) > 0.01) {
          this.singlePriceOption = 'custom';
          this.singleCustomPrice = wishlistData.initialPriceValue;
        } else {
          this.singlePriceOption = 'under';
          this.singleCustomPrice = this.item.price || null;
        }
      } else {
        this.singlePriceOption = 'under';
        this.singleCustomPrice = this.item.price || null;
      }
    }
  }

  getItemTitle(): string {
    if (this.item && this.item.title) {
      const maxLength = 40;
      if (this.item.title.length > maxLength) {
        return this.item.title.substring(0, maxLength) + '...';
      }
      return this.item.title;
    }
    return '';
  }

  getItemImageUrl(): string {
    if (!this.item) {
      return '';
    }
    return this.item.imageLargeUrl || this.item.itemImageURL || this.item.imageMediumUrl || '';
  }

  hasHdPrice(): boolean {
    return this.item && this.item.priceHd != null;
  }

  hasSdPrice(): boolean {
    return this.item && this.item.priceSd != null;
  }

  has4kPrice(): boolean {
    if (!this.item || !this.isVuduStore()) {
      return false;
    }
    // Check for buy or rent 4K price based on purchase type
    const price4k = this.purchaseType === 'rent' ? this.item.rentalPrice4k : this.item.price4k;
    return price4k != null;
  }

  isVuduStore(): boolean {
    return this.store === Global.VUDU;
  }

  /** Returns true if the item is a movie (not a TV season) */
  isMovieItem(): boolean {
    return this.item && this.item.itemType === 'movie';
  }

  /** Returns true if the item only has a single price (no SD/HD distinction) */
  hasSinglePrice(): boolean {
    if (!this.item) {
      return false;
    }
    // Items with SD/HD prices don't use single price
    if (this.item.priceHd != null || this.item.priceSd != null) {
      return false;
    }
    // Check for single price field
    return this.item.price != null;
  }

  /** Returns true if the item is a video item (movie or TV season) with SD/HD prices */
  isVideoItem(): boolean {
    if (!this.item) {
      return false;
    }
    const itemType = this.item.itemType;
    return itemType === 'movie' || itemType === 'season' || itemType === 'tvSeason';
  }

  /** Returns true if this is an iTunes movie with 4K support */
  isItunes4kMovie(): boolean {
    return this.storeIsItunes() && this.isMovieItem() && this.item && this.item.has4K == 1;
  }

  /** Returns true if the store is iTunes/Apple TV */
  storeIsItunes(): boolean {
    return this.store === Global.ITUNES;
  }

  /** Returns the appropriate title for HD price section (HD/4K for iTunes 4K movies) */
  getHdPriceSectionTitle(): string {
    if (this.isItunes4kMovie()) {
      return this.getString('priceAlertHd4kPrice');
    }
    return this.getString('priceAlertHdPrice');
  }

  getFormattedSinglePrice(): string {
    if (this.item && this.item.price) {
      return PriceHelper.formatPrice(this.item.price, this.currency || this.item.currency);
    }
    return '';
  }

  getUnderSinglePriceDisplay(): string {
    if (this.item && this.item.price) {
      return PriceHelper.formatPrice(this.item.price, this.currency || this.item.currency);
    }
    return '---';
  }

  onSinglePriceOptionChange(option: 'under' | 'custom') {
    this.singlePriceOption = option;
  }

  onSingleCustomPriceChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = parseFloat(input.value);
    this.singleCustomPrice = isNaN(value) ? null : value;
  }

  getFormattedHdPrice(): string {
    if (this.item && this.item.priceHd) {
      return PriceHelper.formatPrice(this.item.priceHd, this.currency || this.item.currency);
    }
    return '';
  }

  getFormattedSdPrice(): string {
    if (this.item && this.item.priceSd) {
      return PriceHelper.formatPrice(this.item.priceSd, this.currency || this.item.currency);
    }
    return '';
  }

  getFormatted4kPrice(): string {
    if (this.item) {
      const price4k = this.purchaseType === 'rent' ? this.item.rentalPrice4k : this.item.price4k;
      if (price4k) {
        return PriceHelper.formatPrice(price4k, this.currency || this.item.currency);
      }
    }
    return '';
  }

  getCurrencySymbol(): string {
    return PriceHelper.getCurrencySymbol(this.currency || (this.item && this.item.currency) || 'EUR');
  }

  onHdOptionChange(option: 'under' | 'custom') {
    this.hdPriceOption = option;
  }

  onSdOptionChange(option: 'under' | 'custom' | 'ignore') {
    this.sdPriceOption = option;
  }

  onHdCustomPriceChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = parseFloat(input.value);
    this.hdCustomPrice = isNaN(value) ? null : value;
  }

  onSdCustomPriceChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = parseFloat(input.value);
    this.sdCustomPrice = isNaN(value) ? null : value;
  }

  on4kOptionChange(option: 'under' | 'custom') {
    this.fourKPriceOption = option;
  }

  on4kCustomPriceChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = parseFloat(input.value);
    this.fourKCustomPrice = isNaN(value) ? null : value;
  }

  saveSettings() {
    const settings: PriceAlertSettings = {
      hdPriceOption: this.hdPriceOption,
      hdCustomPrice: this.hdPriceOption === 'custom' ? this.hdCustomPrice : null,
      sdPriceOption: this.sdPriceOption,
      sdCustomPrice: this.sdPriceOption === 'custom' ? this.sdCustomPrice : null,
      purchaseType: this.purchaseType
    };

    // Add 4K settings for Vudu store
    if (this.isVuduStore()) {
      settings.fourKPriceOption = this.fourKPriceOption;
      settings.fourKCustomPrice = this.fourKPriceOption === 'custom' ? this.fourKCustomPrice : null;
    }

    // Add single price settings for non-video items (music, apps, books)
    if (this.hasSinglePrice()) {
      settings.singlePriceOption = this.singlePriceOption;
      settings.singleCustomPrice = this.singlePriceOption === 'custom' ? this.singleCustomPrice : null;
    }

    this.onSave.emit(settings);
    this.close();
  }

  onPurchaseTypeChange(type: 'buy' | 'rent') {
    this.purchaseType = type;
  }

  removeFromWishlist() {
    this.onRemove.emit();
    this.close();
  }

  closeOnBackdropClick(event: MouseEvent) {
    if ((event.target as HTMLElement).classList.contains('modal')) {
      this.close();
    }
  }

  // Get the display price for the "unter" option
  getUnderHdPriceDisplay(): string {
    if (this.item && this.item.priceHd) {
      return PriceHelper.formatPrice(this.item.priceHd, this.currency || this.item.currency);
    }
    return '---';
  }

  getUnderSdPriceDisplay(): string {
    if (this.item && this.item.priceSd) {
      return PriceHelper.formatPrice(this.item.priceSd, this.currency || this.item.currency);
    }
    return '---';
  }

  getUnder4kPriceDisplay(): string {
    if (this.item) {
      const price4k = this.purchaseType === 'rent' ? this.item.rentalPrice4k : this.item.price4k;
      if (price4k) {
        return PriceHelper.formatPrice(price4k, this.currency || this.item.currency);
      }
    }
    return '---';
  }
}
